import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Google, Facebook, Apple, Visibility, VisibilityOff } from '@mui/icons-material';
import { useGoogleLogin } from '@react-oauth/google';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { trackLogin } from '../../services/analyticsService';

const Login = () => {
  const [submitStatus, setSubmitStatus] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register: formRegister, handleSubmit, formState: { errors }, setValue } = useForm(); // Átnevezzük
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loginWithGoogle, loginWithFacebook } = useAuth();
  // A védett oldalról érkezőt bejelentkezés után oda irányítjuk vissza (csak belső útvonal)
  const from = location.state?.from;
  const redirectTo = typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/dashboard';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Ha már be van jelentkezve, irányítsuk át
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  // Regisztrációból érkező üzenet kezelése
  useEffect(() => {
    if (location.state?.message) {
      setSubmitStatus({ 
        type: 'success', 
        message: location.state.message 
      });
      
      // Ha van email a state-ben, töltsük be
      if (location.state.email) {
        setValue('email', location.state.email);
      }
      
      // Töröljük a state-et, hogy ne maradjon meg
      window.history.replaceState({}, document.title);
    }
  }, [location.state, setValue]);

  const onSubmit = async (data) => {
    try {
      setSubmitStatus({ type: 'loading', message: 'Bejelentkezés folyamatban...' });
      
      const result = await login(data.email.trim(), data.password, rememberMe);
      
      if (result.success) {
        trackLogin('email');
        setSubmitStatus({
          type: 'success',
          message: 'Sikeres bejelentkezés! Átirányítás...'
        });

        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 1500);
      } else if (result.unverified) {
        navigate('/activate', { state: { email: data.email, fromLogin: true } });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || 'Hibás email cím vagy jelszó!'
        });
      }
      
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Hiba történt a bejelentkezés során!' 
      });
    }
  };

  const handleSocialResult = useCallback(async (result) => {
    if (result.success && result.newUser) {
      navigate('/social-register', { state: result.profile });
    } else if (result.success) {
      navigate(redirectTo, { replace: true });
    } else {
      setSubmitStatus({ type: 'error', message: result.error || 'Social bejelentkezés sikertelen' });
    }
  }, [navigate, redirectTo]);

  const isGoogleConfigured = !!(
    process.env.REACT_APP_GOOGLE_CLIENT_ID &&
    !process.env.REACT_APP_GOOGLE_CLIENT_ID.startsWith('your-')
  );

  const isFacebookConfigured = !!(
    process.env.REACT_APP_FACEBOOK_APP_ID &&
    !process.env.REACT_APP_FACEBOOK_APP_ID.startsWith('your-')
  );

  const googleLogin = useGoogleLogin({
    scope: 'openid email profile',
    onSuccess: async (tokenResponse) => {
      setSubmitStatus({ type: 'loading', message: 'Google hitelesítés folyamatban...' });
      const result = await loginWithGoogle(tokenResponse.access_token);
      handleSocialResult(result);
    },
    onError: (err) => {
      console.error('Google login error:', err);
      setSubmitStatus({ type: 'error', message: 'Google bejelentkezés megszakadt.' });
    }
  });

  const loadFbSdk = useCallback(() => {
    return new Promise((resolve) => {
      if (window.FB) return resolve();
      window.fbAsyncInit = () => {
        window.FB.init({ appId: process.env.REACT_APP_FACEBOOK_APP_ID, version: 'v19.0' });
        resolve();
      };
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      document.body.appendChild(script);
    });
  }, []);

  const handleFacebookLogin = useCallback(async () => {
    setSubmitStatus({ type: 'loading', message: 'Facebook hitelesítés folyamatban...' });
    await loadFbSdk();
    window.FB.login((response) => {
      if (response.authResponse) {
        const { accessToken, userID } = response.authResponse;
        loginWithFacebook(accessToken, userID).then(handleSocialResult);
      } else {
        setSubmitStatus({ type: 'error', message: 'Facebook bejelentkezés megszakadt.' });
      }
    }, { scope: 'email,public_profile' });
  }, [loadFbSdk, loginWithFacebook, handleSocialResult]);

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Bejelentkezés
        </Typography>
        
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Jelentkezz be a fiókodba
        </Typography>

        {/* Social Login gombok */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Google />}
                onClick={() => isGoogleConfigured ? googleLogin() : setSubmitStatus({ type: 'info', message: 'Google bejelentkezés hamarosan elérhető!' })}
                sx={{ py: 1.5 }}
                disabled={submitStatus?.type === 'loading'}
              >
                Bejelentkezés Google-lel
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Facebook />}
                onClick={isFacebookConfigured ? handleFacebookLogin : () => setSubmitStatus({ type: 'info', message: 'Facebook bejelentkezés hamarosan elérhető!' })}
                sx={{ py: 1.5 }}
                disabled={submitStatus?.type === 'loading'}
              >
                Bejelentkezés Facebook-kal
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            vagy
          </Typography>
        </Divider>

        {/* Email/jelszó bejelentkezés */}
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label="Email cím"
            type="email"
            margin="normal"
            {...formRegister('email', {
              required: 'Az email cím megadása kötelező',
              validate: (value) => {
                const trimmed = value.trim();
                return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(trimmed) || 'Érvénytelen email cím';
              }
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            disabled={submitStatus?.type === 'loading'}
          />

          <TextField
            fullWidth
            label="Jelszó"
            type={showPassword ? 'text' : 'password'}
            margin="normal"
            {...formRegister('password', {
              required: 'A jelszó megadása kötelező'
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={submitStatus?.type === 'loading'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(prev => !prev)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Emlékezz rám és elfelejtett jelszó */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 2, 
            mb: 3 
          }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={submitStatus?.type === 'loading'}
                />
              }
              label="Emlékezz rám"
            />
            <Button 
              variant="text" 
              size="small"
              onClick={handleForgotPassword}
              disabled={submitStatus?.type === 'loading'}
            >
              Elfelejtett jelszó?
            </Button>
          </Box>

          {/* Bejelentkezés gomb */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={submitStatus?.type === 'loading'}
            sx={{ py: 1.5, mb: 2 }}
          >
            {submitStatus?.type === 'loading' ? 'Bejelentkezés...' : 'Bejelentkezés'}
          </Button>

          {/* Hibaüzenet a gomb alatt */}
          {submitStatus && (
            <Alert
              severity={
                submitStatus.type === 'error' ? 'error' :
                submitStatus.type === 'success' ? 'success' : 'info'
              }
              sx={{ mb: 2 }}
            >
              {submitStatus.message}
            </Alert>
          )}
        </Box>

        {/* Regisztráció link */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2">
            Még nincs fiókod?{' '}
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <Button variant="text" disabled={submitStatus?.type === 'loading'}>
                Regisztráció
              </Button>
            </Link>
          </Typography>
        </Box>

      </Paper>
    </Container>
  );
};

export default Login;